<script setup>
/**
 * WHAT IF SOMETHING GOES WRONG — every failure arm in the task, on one screen, each with an
 * answer already chosen.
 *
 * ── WHAT IS A CONTROL HERE, AND WHAT IS A REASSURANCE ─────────────────────────────────
 * Three things can go wrong in a way the OWNER decides the answer to: a live list can come
 * back empty (`ifNone`, which the schema makes REQUIRED), an action can fail (`onFailed`,
 * whose absence IS the hand-over), and a jump back has to un-ask something or it loops.
 * Those are the rows.
 *
 * A customer who will not answer, and a turn the assistant could not read, are NOT rows.
 * `types.ts:216` and `:218-222` — three refusals on one step, or two unreadable turns in a
 * row, hand the chat to a person. That is engine behaviour, and an earlier draft of this
 * screen put a control on it: a branch that would have written nowhere, on the headline
 * screen of the redesign. It is a sentence at the bottom instead.
 *
 * ── THE FORGET-LIST IS A PROPOSAL, AND THE OVERRIDE IS WHAT CARRIES FIDELITY ──────────
 * `clears` fires on ARRIVAL and is the only way a conversation goes backwards: the walk
 * skips every step whose answer is already saved, so a jump back to "which day" lands on
 * the very answer that sent the customer down the empty branch. The owner never writes it —
 * she says "ask Which day again", and `helpers/task-spine.js` works out what to forget.
 *
 * It worked out four of the five lists on the real sixteen-step booking task exactly. The
 * fifth is a judgement about a shop, not a mechanism. So the generated list is SHOWN as a
 * sentence and `[change]` pins it: an explicit `row.clears` is written verbatim and never
 * regenerated, which is what makes the round trip exact rather than nearly exact. "Use what
 * Waslo works out" hands it back.
 *
 * ── AND THE STEPS IT IS ABOUT ARE ON IT ───────────────────────────────────────────────
 * A blind reader: "'The steps' and 'What if' were cut apart, and the what-if is ABOUT the
 * steps — on screen four I cannot look back at step two without losing my place." He is
 * right about the coupling and the fix is not to merge the two screens: this screen asks
 * about a handful of arms and the Steps screen is a thirty-row editor, and putting the
 * editor here would make the harder screen of the two the one you answer questions on.
 *
 * What was missing is the REFERENT. Every question here names a step — "«Check the
 * calendar» did not go through" — and a name is only a referent if you can see where it
 * sits. So the list is on the screen, in order, read-only, with each arm carrying the
 * number of the step it belongs to. It is the ordered list as a REMINDER, not as a second
 * place to edit one: nothing here is clickable, because a half-editor on the wrong screen
 * is how two screens start disagreeing about the same row.
 */
import { computed, ref } from 'vue';

import SystemAnswerBox from './SystemAnswerBox.vue';
import TaskStepClears from '../TaskStepClears.vue';
import TaskStepLink from '../TaskStepLink.vue';
import { useWasloI18n } from '../../i18n.js';
import { goToStep, skipWhenWorks } from '../../helpers/task-spine.js';
import { writtenKeys } from '../../helpers/task-flow-draft.js';

const props = defineProps({
  /** The spine's rows, in order. */
  rows: { type: Array, default: () => [] },
  /** The compiled graph, so the generated forget-lists can be read back off it. */
  definition: { type: Object, default: null },
  /** P-2.11: `{ [connectionName]: samplePaths }`, for the sample-based field picker. */
  samplePaths: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:row']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.WHATIF.${name}`;

const open = ref('');
const toggle = name => {
  open.value = open.value === name ? '' : name;
};

const titleOf = step => {
  if (step?.type === 'ask' || step?.type === 'choose') {
    return (step.questionEn ?? '').trim() || (step.name ?? '').trim();
  }
  return (step?.name ?? '').trim() || (step?.instruction ?? '').trim();
};

const stepChoices = computed(() =>
  props.rows.map(row => ({
    id: row.step?.id,
    name: titleOf(row.step) || row.step?.id,
    type: row.step?.type,
  }))
);

const candidates = computed(() => writtenKeys(props.definition));

const keyName = entry =>
  candidates.value.find(row => row.key === entry)?.name ?? entry;

/** Every arm an owner answers, in list order, each already pointing somewhere. */
const arms = computed(() => {
  const out = [];
  props.rows.forEach((row, index) => {
    const step = row.step ?? {};
    if (step.type === 'choose_live') {
      out.push({
        id: `${step.id}:ifnone`,
        index,
        slot: 'ifNone',
        at: t(key('AT_STEP'), { number: index + 1 }),
        question: t(key('LIVE_EMPTY'), { name: titleOf(step) || step.id }),
        target: row.ifNone?.go === 'step' ? row.ifNone.step : '',
      });
    }
    if (step.type === 'call') {
      out.push({
        id: `${step.id}:iffailed`,
        index,
        slot: 'ifFailed',
        at: t(key('AT_STEP'), { number: index + 1 }),
        question: t(key('CALL_FAILED'), { name: titleOf(step) || step.id }),
        target: row.ifFailed?.go === 'step' ? row.ifFailed.step : '',
      });
    }
  });
  return out;
});

/**
 * The ordered list, read-only, so a name in a question below has somewhere to point.
 *
 * `armCount` is on each row rather than a separate lookup: the rows an owner is about to
 * answer questions about are the ones worth marking, and marking them here is what turns a
 * list of thirty into "these two are the ones this screen is about".
 */
const reminder = computed(() =>
  props.rows.map((row, index) => ({
    id: row.step?.id ?? String(index),
    number: index + 1,
    name: titleOf(row.step) || row.step?.id || '',
    asked: arms.value.some(arm => arm.index === index),
  }))
);

const setArm = (arm, value) => {
  const row = props.rows[arm.index];
  const next = { ...row, [arm.slot]: value ? goToStep(value) : undefined };
  emit('update:row', { index: arm.index, row: next });
  // P-2.2: when the Call works it must not run into the rows set up for when it fails. Pointing
  // "if it didn't go through" at a row below it writes where success goes instead.
  if (arm.slot !== 'ifFailed' || !value) return;
  const failAt = props.rows.findIndex(r => r.step?.id === value);
  if (failAt <= arm.index) return;
  const rows = props.rows.map((r, i) => (i === arm.index ? next : r));
  const skipped = skipWhenWorks(rows, arm.index, failAt);
  if (skipped !== rows) {
    emit('update:row', { index: failAt - 1, row: skipped[failAt - 1] });
  }
};

/** A Call's own system-answer settings changed (P-10.6, P-10.21). */
const setStep = (arm, step) => {
  const row = props.rows[arm.index];
  emit('update:row', { index: arm.index, row: { ...row, step } });
};

/** Where a Call's success goes, said on its arm (P-2.2): a step, or the end of the task. */
const worksLine = arm => {
  const id = props.rows[arm.index]?.step?.id;
  const step = (props.definition?.steps ?? []).find(s => s.id === id);
  const to = step?.onWorked;
  if (!to) return t(key('WORKS_ENDS'));
  const target = props.rows.find(r => r.step?.id === to)?.step;
  return t(key('WORKS_GOES_ON'), { step: titleOf(target) || to });
};

/** Where the chat can come BACK to, and what arriving there forgets. */
const forgets = computed(() => {
  const compiled = new Map(
    (props.definition?.steps ?? []).map(step => [step.id, step.clears ?? []])
  );
  return props.rows
    .map((row, index) => ({
      index,
      id: row.step?.id ?? '',
      name: titleOf(row.step) || row.step?.id,
      keys: compiled.get(row.step?.id) ?? [],
      pinned: Array.isArray(row.clears),
    }))
    .filter(row => row.keys.length > 0 || row.pinned);
});

const setClears = (index, value) => {
  const row = props.rows[index];
  emit('update:row', { index, row: { ...row, clears: [...value] } });
};

const unpin = index => {
  const row = props.rows[index];
  emit('update:row', { index, row: { ...row, clears: null } });
};
</script>

<template>
  <section class="flex flex-col gap-6" data-waslo-whatif-screen>
    <div class="flex flex-col gap-1">
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </h2>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('DESCRIPTION')) }}
      </p>
    </div>

    <p
      v-if="arms.length === 0 && forgets.length === 0"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-whatif-none
    >
      {{ t(key('NOTHING')) }}
    </p>

    <!-- The steps these questions are about, so a name below has a referent. -->
    <div
      v-if="reminder.length"
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-whatif-steps
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('STEPS_HEADING')) }}
      </p>
      <ol class="flex flex-col gap-1 mb-0">
        <li
          v-for="row in reminder"
          :key="row.id"
          class="flex items-start gap-2 text-sm"
          :class="row.asked ? 'text-n-slate-12' : 'text-n-slate-11'"
          :data-waslo-whatif-step="row.id"
          :data-asked="row.asked ? 'yes' : 'no'"
        >
          <span class="tabular-nums shrink-0" aria-hidden="true">
            {{ row.number }}.
          </span>
          <bdi class="min-w-0">{{ row.name }}</bdi>
        </li>
      </ol>
    </div>

    <div
      v-for="arm in arms"
      :key="arm.id"
      class="flex flex-col gap-2"
      :data-waslo-whatif-arm="arm.id"
    >
      <p class="mb-0 text-sm text-n-slate-11" :data-waslo-whatif-at="arm.id">
        {{ arm.at }}
      </p>
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        <bdi>{{ arm.question }}</bdi>
      </p>
      <TaskStepLink
        :model-value="arm.target"
        :label="t(key('ANSWER'))"
        :steps="stepChoices"
        :exclude="rows[arm.index]?.step?.id"
        :empty-label="t(key('HANDOVER'))"
        :data-waslo-whatif-target="arm.id"
        @update:model-value="value => setArm(arm, value)"
      />
      <p
        v-if="arm.slot === 'ifFailed'"
        class="mb-0 text-sm text-n-slate-11"
        :data-waslo-whatif-works="arm.id"
      >
        <bdi>{{ worksLine(arm) }}</bdi>
      </p>
      <template v-if="arm.slot === 'ifFailed'">
        <button
          type="button"
          class="self-start text-sm underline text-n-blue-11"
          :aria-expanded="open === arm.id"
          :data-waslo-whatif-system="arm.id"
          @click="toggle(arm.id)"
        >
          {{ t(key('SYSTEM.TOGGLE')) }}
        </button>
        <SystemAnswerBox
          v-if="open === arm.id"
          :step="rows[arm.index].step"
          :steps="stepChoices"
          :sample-paths="samplePaths[rows[arm.index].step?.connection] ?? []"
          @update:step="value => setStep(arm, value)"
        />
      </template>
    </div>

    <!-- What arriving somewhere again un-asks. Generated, shown, and pinnable. -->
    <div
      v-for="row in forgets"
      :key="`forget-${row.id}`"
      class="flex flex-col gap-2"
      :data-waslo-whatif-forget="row.id"
    >
      <button
        type="button"
        class="flex items-start w-full gap-2 px-3 py-2 text-sm rounded-lg text-start text-n-slate-12 hover:bg-n-alpha-2"
        :aria-expanded="open === row.id"
        :data-waslo-whatif-forget-toggle="row.id"
        @click="toggle(row.id)"
      >
        <span
          class="mt-0.5 shrink-0 size-4 text-n-slate-11"
          :class="
            open === row.id ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'
          "
          aria-hidden="true"
        />
        <span class="flex-1 min-w-0">
          <bdi>{{
            t(key('FORGETS'), {
              name: row.name,
              keys: row.keys
                .map(keyName)
                .join(t('WASLO.COMMON.LIST_SEPARATOR')),
            })
          }}</bdi>
        </span>
        <span
          v-if="row.pinned"
          class="text-n-slate-11"
          data-waslo-whatif-pinned
        >
          {{ t(key('PINNED')) }}
        </span>
      </button>
      <div v-if="open === row.id" class="flex flex-col gap-3 px-3 pb-2">
        <TaskStepClears
          :model-value="row.keys"
          :candidates="candidates"
          own-key=""
          @update:model-value="value => setClears(row.index, value)"
        />
        <button
          v-if="row.pinned"
          type="button"
          class="self-start text-sm underline text-n-blue-11"
          :data-waslo-whatif-unpin="row.id"
          @click="unpin(row.index)"
        >
          {{ t(key('UNPIN')) }}
        </button>
      </div>
    </div>

    <div
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-whatif-engine
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('ENGINE')) }}
      </p>
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('ENGINE_REFUSED')) }}</p>
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('ENGINE_UNREAD')) }}</p>
    </div>
  </section>
</template>
